import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { AuthStateService } from '@hypenation/auth';
import { switchMap, of } from 'rxjs';
import {
  CustomerEventSummary,
  MyTicketsLoaderService,
} from '../../services/my-tickets-loader.service';

/**
 * The events this customer holds tickets for — one card per event, opening that event's tickets.
 *
 * This is the sustainable shape for wallets. Filtering My Tickets by event was tried first and does
 * not hold up: the filter row grows with every event a customer has ever attended, so it degrades
 * exactly as someone uses the platform more. An event list paginates and stays readable, and it puts
 * the balance where the customer is already thinking about one event.
 *
 * Distinct from `/events`, which is the browse-and-buy catalogue.
 */
@Component({
  selector: 'hypenation-my-events',
  standalone: true,
  imports: [CommonModule, RouterModule, NzSpinModule, NzButtonModule, NzIconModule],
  templateUrl: './my-events.component.html',
  styleUrl: './my-events.component.css',
})
export class MyEventsComponent implements OnInit {
  loading = false;
  events: CustomerEventSummary[] = [];

  private readonly loader = inject(MyTicketsLoaderService);
  private readonly authStateService = inject(AuthStateService);

  ngOnInit(): void {
    const email = this.authStateService.getUserEmail();
    if (!email) return;

    this.loading = true;
    this.loader
      .loadTickets(email)
      .pipe(
        switchMap((tickets) =>
          this.loader
            .loadWalletBalances(tickets)
            .pipe(
              switchMap((wallets) => of(this.loader.summariseByEvent(tickets, wallets)))
            )
        )
      )
      .subscribe({
        next: (events) => {
          this.events = events;
          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }
}
